// Shared module top bar. One of only three ink surfaces in the system (with the
// taxonomy preview strip and the SQL editor pane). Styling lives in orbit.css —
// `.o-topnav` and friends — so hover, the brass underline reveal, and the
// focus ring are real CSS states rather than JS handlers.

import AppLauncher from './AppLauncher.jsx';
import { useId } from 'react';
import { byId } from '../modules/registry.js';
import ImpersonationBadge from '../auth/ImpersonationBadge.jsx';
import WorkspaceSelector from '../workspace/WorkspaceSelector.jsx';
import { T } from '../styles/tokens.js';

function goToHub() {
  history.pushState(null, '', '/');
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// The Orbit mark: a planet crossed by a brass ring, with six satellites on spokes.
//
// GEOMETRY IS MEASURED, NOT DRAWN BY EYE. Every number below was fitted off the supplied
// artwork (Gemini_Generated_Image_s8p07as8p07as8p0.png, 224x232) and normalised to this
// 64-unit viewBox: the satellite orbit came from a circumcentre through the three brass
// satellites, the planet from a least-squares circle on its outer rim, and the ring from a
// direct ellipse fit to the gold band's inner and outer edges. Two independent checks agree
// — ORBIT + SAT lands on exactly 32.0, and the fitted planet centre sits on the orbit centre
// to within 0.03 units. Do not nudge these by hand; re-measure if the artwork changes.
//
// Two tones, because the mark appears on both ink and paper. `onInk` (the default) draws the
// planet and half the satellites in paper for the dark top bar; `onInk={false}` uses ink for
// light surfaces like the login card. The ring stays brass either way — it is the one colour
// that reads on both. Getting this wrong is not subtle: an ink planet on the ink top bar is
// invisible, which is exactly what shipped before this variant existed.
//
// Colours come from the tokens rather than hex, so the mark cannot drift out of the palette.
// The artwork's gold matches brass-lift to within a rounding error.
//
// `detail={false}` drops the spokes and satellites for small renders — below roughly 20px
// they collapse into noise — and scales the planet and ring up to fill the box instead.
export function OrbitMark({ size = 24, detail = true, onInk = true }) {
  const body    = onInk ? T.textInvert : T.ink;
  const surface = onInk ? T.ink : T.surface;
  const clipId  = useId();

  const PLANET = 12.65;   // planet radius
  const SAT    = 4.2;     // satellite radius
  const ORBIT  = 27.8;    // satellite orbit radius  (ORBIT + SAT === 32, the viewBox edge)
  const RX     = 18.3;    // ring semi-major, centreline
  const RY     = 4.15;    // ring semi-minor, centreline
  const TILT   = -30.5;   // degrees

  // Without the satellites the mark is only as wide as the ring, so scale it up to the box.
  const k = detail ? 1 : 32 / (RX + 1.1);

  // The artwork's stroke widths do not survive being shrunk to a favicon: the ring measures
  // 1.79 units (0.67px at 24px) and the spokes 0.56 (0.21px), both of which the browser
  // renders as a grey smear or drops entirely. These are thickened deliberately — the only
  // place this mark departs from the measurements, and the alternative is an invisible ring.
  const stroke = detail ? 2.4 : 4.2;
  const angles = [-90, -30, 30, 90, 150, 210];   // six spokes, evenly spaced
  const pt = (deg) => [
    32 + ORBIT * Math.cos((deg * Math.PI) / 180),
    32 + ORBIT * Math.sin((deg * Math.PI) / 180),
  ];

  // Brass at -90, body at -30, alternating — matching the artwork, where the top, lower-left
  // and lower-right satellites are gold and the other three are paper.
  const ring = (extra) => (
    <ellipse cx="32" cy="32" rx={RX * k} ry={RY * k} fill="none"
             stroke={extra ? surface : T.brassLift} strokeWidth={stroke + (extra || 0)}
             transform={`rotate(${TILT} 32 32)`} />
  );

  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true"
         style={{ display: 'block', flexShrink: 0 }}>
      {detail && (
        <g stroke={body} strokeWidth="1.6" strokeLinecap="round" opacity={onInk ? 0.85 : 1}>
          {angles.map((a) => {
            const [x, y] = pt(a);
            return <line key={a} x1="32" y1="32" x2={x} y2={y} />;
          })}
        </g>
      )}

      {/* Ring, far half: drawn before the planet so the planet occludes it. */}
      {ring()}

      <circle cx="32" cy="32" r={PLANET * k} fill={body} />

      {/* Ring, near half: the same ellipse clipped to the half in front, so it crosses over
          the planet on the near side. Drawn whole on top instead, it reads as a band painted
          across a ball rather than a ring around one — which is the whole silhouette. The
          surface-coloured pass underneath reproduces the knockout gap the artwork uses to
          separate the gold from the planet where the two overlap.

          The clip rect carries the SAME rotation as the ring. The near/far boundary of a
          tilted ring is its major axis, not the screen equator — clipping on a horizontal
          edge cuts the band into pieces wherever the tilt lifts it back above centre, and the
          gold ends up crossing the planet's face in two disconnected stubs. */}
      <defs>
        <clipPath id={clipId}>
          <rect x="-32" y="32" width="128" height="64"
                transform={`rotate(${TILT} 32 32)`} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {ring(1.6)}
        {ring()}
      </g>

      {detail && angles.map((a, i) => {
        const [x, y] = pt(a);
        return <circle key={a} cx={x} cy={y} r={SAT}
                       fill={i % 2 === 0 ? T.brassLift : body} />;
      })}
    </svg>
  );
}

// `right` renders extra controls before the Back to Hub button (e.g. Competitive
// Monitoring's client switcher). `showHub={false}` suppresses the button entirely
// for modules that are entered directly rather than through the Hub.
export default function OrbitModuleHeader({
  title = 'Orbit', tabs, activeTab, onTabChange, navigation, right, showHub = true, moduleId,
}) {
  // Whether this module gets a workspace selector is a property of its registration, not a
  // decision taken here or in each module — a future module opts in by setting clientScoped.
  const scoped = !!moduleId && !!byId[moduleId]?.clientScoped;
  return (
    <nav className={navigation || tabs?.length ? "o-topnav o-module-nav" : "o-topnav"}>
      <div className="o-topnav-start">
        <AppLauncher />
        <div className="o-brand">
          <OrbitMark />
          <span>{title}</span>
        </div>
      </div>

      {navigation || (tabs && tabs.length > 0 && (
        <div className="o-navlinks">
          {tabs.map(t => (
            <button
              key={t.id}
              className="o-navlink"
              aria-current={activeTab === t.id ? 'page' : 'false'}
              onClick={() => onTabChange(t.id)}
            >{t.label}</button>
          ))}
        </div>
      ))}

      {/* Always mounted, renders nothing unless impersonating. Ahead of the workspace selector
          because it changes what that selector is even listing. */}
      <div className="o-topnav-right">
        <ImpersonationBadge />
        {scoped && <WorkspaceSelector />}
        {right}
      </div>
      {showHub && <button className="o-hub" onClick={goToHub}>‹ Back to Hub</button>}
    </nav>
  );
}
